import { clear, h } from "./dom";
import { lang, langs, t } from "./i18n";
import { renderModes } from "./modes";
import { renderPicker } from "./picker";
import { renderViewer } from "./viewer";

// ---------- theme (light/dark, website palette) ----------
function initTheme(): void {
  const stored = localStorage.getItem("demo_theme");
  const preferred = window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  document.documentElement.dataset.theme = stored === "light" || stored === "dark" ? stored : preferred;
}

function toggleTheme(): void {
  const next = document.documentElement.dataset.theme === "dark" ? "light" : "dark";
  document.documentElement.dataset.theme = next;
  localStorage.setItem("demo_theme", next);
}

// ---------- chrome ----------
function buildChrome(): HTMLElement {
  const langLinks = langs
    .filter((l) => l !== lang)
    .map((l) => h("a", { className: "chrome-btn", href: `/${l}/${location.hash}` }, l.toUpperCase()));
  return h(
    "header",
    { className: "chrome" },
    h("a", { className: "brand", href: `https://treeandterrain.com/${lang}/` }, t("brand")),
    h("span", { className: "chrome-title" }, t("title")),
    h("span", { className: "spacer" }),
    h("a", { className: "chrome-btn back-btn hidden", href: "#/" }, t("viewer.back")),
    ...langLinks,
    h("button", { className: "chrome-btn", title: t("theme.toggle"), onClick: toggleTheme }, "◐"),
  );
}

// ---------- router ----------
let cleanup: (() => void) | null = null;

function route(view: HTMLElement, header: HTMLElement): void {
  cleanup?.();
  const hash = location.hash;
  let m: RegExpExecArray | null;
  let back: string | null = null;
  if ((m = /^#\/3d\/([a-z0-9-]+)/.exec(hash))) {
    back = "#/3d";
    // three.js and the point cloud machinery load on demand — 2D visitors
    // never fetch them (esbuild code splitting)
    const slug = m[1];
    let cancelled = false;
    let inner: (() => void) | null = null;
    cleanup = () => {
      cancelled = true;
      inner?.();
    };
    clear(view).append(h("p", { className: "muted viewer3d-loading" }, t("viewer.loading")));
    void import("./viewer3d").then(
      (mod) => {
        if (!cancelled) inner = mod.renderViewer3d(view, slug);
      },
      () => {
        if (!cancelled) clear(view).append(h("p", { className: "error viewer3d-loading" }, t("error.load")));
      },
    );
  } else if ((m = /^#\/r\/([a-z0-9-]+)/.exec(hash))) {
    back = "#/2d";
    cleanup = renderViewer(view, m[1]);
  } else if (/^#\/(2d|3d)/.test(hash)) {
    back = "#/";
    cleanup = renderPicker(view, hash.startsWith("#/3d") ? "3d" : "2d");
  } else {
    cleanup = renderModes(view);
  }
  const btn = header.querySelector<HTMLAnchorElement>(".back-btn");
  if (btn) {
    btn.classList.toggle("hidden", !back);
    if (back) btn.href = back;
  }
  for (const a of header.querySelectorAll<HTMLAnchorElement>("a.chrome-btn:not(.back-btn)")) {
    a.href = `/${a.textContent!.toLowerCase()}/${location.hash}`;
  }
}

initTheme();
document.title = t("title");
const header = buildChrome();
const view = h("main", { className: "view" });
document.getElementById("app")!.append(header, view);
window.addEventListener("hashchange", () => route(view, header));
route(view, header);
